"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

function optionIndex(options, value) {
  return options.findIndex((option) => String(option.value) === String(value));
}

/**
 * A select-only listbox that is intentionally portalled so dashboard filters are
 * not clipped by cards, sticky headers, or full-screen shells.
 */
export default function CustomSelect({
  value,
  options = [],
  onChange,
  ariaLabel,
  id,
  className = "",
  disabled = false,
  surface = "muted",
  density = "comfortable",
  menuClassName = "",
  placeholder = "Select an option",
}) {
  const normalizedOptions = useMemo(
    () => (Array.isArray(options) ? options.filter((option) => option && option.value !== undefined) : []),
    [options]
  );
  const selectedOptionIndex = optionIndex(normalizedOptions, value);
  const selectedIndex = Math.max(0, selectedOptionIndex);
  const selectedOption = normalizedOptions[selectedOptionIndex] || null;
  const generatedId = useId();
  const buttonId = id || `custom-select-${generatedId}`;
  const listboxId = `${buttonId}-listbox`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const typeaheadRef = useRef({ value: "", timeoutId: null });
  const suppressClickRef = useRef(false);
  const surfaceClassName =
    surface === "dark"
      ? "border-slate-700 bg-slate-800 text-slate-100 hover:border-slate-600"
      : surface === "white"
        ? "border-slate-200 bg-white text-slate-900 hover:border-slate-300"
        : "border-slate-200 bg-slate-50 text-slate-900 hover:border-slate-300";
  const densityClassName =
    density === "compact" ? "h-10 px-3 py-2" : "min-h-10 px-3 py-2.5";

  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (!open) setMenuStyle(null);
  }, [open]);

  useEffect(
    () => () => {
      if (typeaheadRef.current.timeoutId) window.clearTimeout(typeaheadRef.current.timeoutId);
    },
    []
  );

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const trigger = buttonRef.current;
      if (!trigger || typeof window === "undefined") return;
      const rect = trigger.getBoundingClientRect();
      const padding = 8;
      const gap = 6;
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const maxWidth = Math.max(0, viewportWidth - padding * 2);
      const width = Math.min(Math.max(rect.width, 180), maxWidth);
      const left = Math.min(Math.max(viewportLeft + padding, rect.left), viewportRight - width - padding);
      const below = Math.max(0, viewportBottom - rect.bottom - padding - gap);
      const above = Math.max(0, rect.top - viewportTop - padding - gap);
      const openAbove = below < 160 && above > below;
      const availableHeight = Math.min(320, openAbove ? above : below);

      setMenuStyle(
        openAbove
          ? { position: "fixed", bottom: Math.max(padding, window.innerHeight - rect.top + gap), left, width, maxHeight: availableHeight }
          : { position: "fixed", top: rect.bottom + gap, left, width, maxHeight: availableHeight }
      );
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!buttonRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [open]);

  const choose = (index) => {
    const option = normalizedOptions[index];
    if (!option || option.disabled) return;
    onChange?.(option.value);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const moveActive = (nextIndex, direction = 1) => {
    if (!normalizedOptions.length) return;
    let index = nextIndex;
    for (let attempts = 0; attempts < normalizedOptions.length; attempts += 1) {
      const candidate = normalizedOptions[(index + normalizedOptions.length) % normalizedOptions.length];
      if (!candidate.disabled) {
        setActiveIndex((index + normalizedOptions.length) % normalizedOptions.length);
        return;
      }
      index += direction;
    }
  };

  const handleKeyDown = (event) => {
    if (disabled || !normalizedOptions.length) return;
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(selectedIndex);
      } else {
        moveActive(activeIndex + 1);
      }
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(selectedIndex);
      } else {
        moveActive(activeIndex - 1, -1);
      }
      return;
    }
    if (event.key === "Home" && open) {
      event.preventDefault();
      moveActive(0, 1);
      return;
    }
    if (event.key === "End" && open) {
      event.preventDefault();
      moveActive(normalizedOptions.length - 1, -1);
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
      if (open) choose(activeIndex);
      else {
        setOpen(true);
        setActiveIndex(selectedIndex);
      }
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (open) choose(activeIndex);
      else {
        setOpen(true);
        setActiveIndex(selectedIndex);
      }
      return;
    }
    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const nextValue = `${typeaheadRef.current.value}${event.key}`.toLocaleLowerCase();
      const matchingIndex = normalizedOptions.findIndex(
        (option) =>
          !option.disabled && String(option.label || "").toLocaleLowerCase().startsWith(nextValue)
      );
      if (matchingIndex < 0) return;
      event.preventDefault();
      typeaheadRef.current.value = nextValue;
      if (typeaheadRef.current.timeoutId) window.clearTimeout(typeaheadRef.current.timeoutId);
      typeaheadRef.current.timeoutId = window.setTimeout(() => {
        typeaheadRef.current = { value: "", timeoutId: null };
      }, 500);
      setActiveIndex(matchingIndex);
      if (!open) setOpen(true);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        disabled={disabled}
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="none"
        aria-activedescendant={open ? `${listboxId}-option-${activeIndex}` : undefined}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          if (disabled || !normalizedOptions.length) return;
          setOpen((current) => !current);
          setActiveIndex(selectedIndex);
        }}
        onKeyDown={handleKeyDown}
        className={`flex min-w-0 items-center justify-between gap-2 rounded-xl border text-left text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-violet-200 disabled:cursor-not-allowed disabled:opacity-60 ${surfaceClassName} ${densityClassName} ${className}`}
      >
        <span className="min-w-0 flex-1 truncate">{selectedOption?.label || placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && menuStyle && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              className={`z-[6200] overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_44px_-22px_rgba(15,23,42,0.45)] ${menuClassName}`}
              style={menuStyle}
            >
              <div
                id={listboxId}
                role="listbox"
                aria-label={ariaLabel}
                className="overflow-y-auto"
                style={{ maxHeight: Math.max(0, Number(menuStyle.maxHeight || 0) - 10) }}
              >
                {normalizedOptions.map((option, index) => {
                  const selected = String(option.value) === String(value);
                  const active = activeIndex === index;
                  return (
                    <button
                      key={String(option.value)}
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      id={`${listboxId}-option-${index}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={option.disabled}
                      tabIndex={-1}
                      onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                      onClick={() => choose(index)}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition focus:outline-none ${
                        selected || active
                          ? "bg-violet-50 text-slate-950"
                          : "text-slate-700 hover:bg-violet-50/70"
                      } ${option.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                      <span className="min-w-0 truncate">{option.label}</span>
                      {selected ? <Check className="h-4 w-4 shrink-0 text-violet-700" aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
